// The timesheet, as a timesheet.
//
// ── What was here, measured ────────────────────────────────────────────────
//
// The owner, 2026-09-10, on /approve/:id: "how the fuck does this say
// timesheets!! just shows a cluster fuck of jobs and nothing that makes
// sense!! what am I approving, how can I tell what is what?!! what if a job
// needs to be removed .. where do I see the hours, the amount in dollars".
//
// Driven in a browser at 320px on a seeded week — 9 lines, 4 jobs, 5 days —
// the screen rendered the heading "LINES · 9" and nine identical cards:
//
//     Mon 7 / Mow & edge — Maple Court / Harbour Ridge HOA · 2:45
//
// The only totals anywhere on it were "Clock 25:30 · Sheet 28:00" and the
// hours on the Approve button. No per-day total. No per-job total. No dollar
// figure for the week at all — an approver was being asked to authorise a
// payment whose amount was not on the screen. At 1440px it was the SAME
// single column, nine cards 1208px wide: there was no desktop layout to fail
// at, because there was no layout.
//
// ── The shape, and why it is this one ──────────────────────────────────────
//
// Every weekly timesheet anybody has ever signed — Deputy, When I Work,
// QuickBooks Time, Connecteam, and the paper ones they descend from — is a
// GRID: rows are the thing worked on, columns are the days, cells are hours,
// and the totals sit on BOTH edges with a grand total in the corner. The
// edges are the entire point. A person does not check a timesheet by reading
// nine cards; they read down the right for "is this job's week right" and
// along the bottom for "is Tuesday right", and the corner is what they are
// signing. docs/TIMESHEET_DESIGN.md records what was looked at and what was
// rejected.
//
// ── Seven columns do not fit on a phone, and overflow-x is not the answer ──
//
// docs/APP_REBUILD.md §2: no horizontal scroll in ANY container, at 320px —
// "an overflow-x container is not a solution to content that does not fit; it
// is the bug wearing a scrollbar". So the narrow form is not this grid made
// smaller. It is the grid TRANSPOSED into the axis a phone has: one group per
// day, each headed by that day's own total (the column edge), with a compact
// by-job summary above them (the row edge) and the corner pinned at the top.
// Both edges, both widths, nothing panning.
//
// The switch is CONTAINER width via `useNarrowContainer`, not viewport width,
// and the threshold is RecordList's own — the single 768 that usePhoneLayout,
// DesktopShell.css and --fw-phone-max already agree on. No new breakpoint
// (spec §6), and no `overflow-x` anywhere in the stylesheet.
//
// ── It computes NOTHING ────────────────────────────────────────────────────
//
// Every figure here — each cell, each row total, each day total, the corner —
// is a number `server/utils/timesheetGrid.js` produced by calling
// `paystubCalc.computeFigures`, the function that writes the paystub, over a
// subset of these exact lines. This file formats and positions them. It does
// not add, multiply or round a single one.
//
// That is not a style preference. #202 was a $500 fixed-price job grossed as
// 8 × $500 = $4,000, and it stayed silent for months precisely because the
// screen ran its own arithmetic and so never contradicted the paystub.
// `TimesheetGrid.test.js` enforces it the only way that actually works: it
// hands the component a grid whose corner is NOT the sum of its own columns
// and requires the component to draw the corner it was given.

import React, { useRef } from 'react';
import { SectionLabel, Notice } from '../../../ui/fw';
// Deep import, deliberately: `useNarrowContainer` is not on the fw barrel and
// putting it there would make this change touch ui/, which is Tier C. The two
// other deep imports of the same kind are AppRouter.js and AppShell.js, both
// reaching usePhoneLayout for the same one breakpoint.
import { useNarrowContainer } from '../../../ui/fw/components/RecordList.js';
import { dayShort, dayLong, hoursClock, moneyText } from '../lib/format';
import './TimesheetGrid.css';

const list = (a) => (Array.isArray(a) ? a : []);

/**
 * An hours figure in a cell, or a dash.
 *
 * A day nobody worked reads "—", never "0:00". On the screen that becomes
 * payroll, a zero is a claim that somebody was there and logged nothing —
 * which is a different thing from not being there, and the two must not
 * render identically.
 */
function cellHours(hours) {
  const n = Number(hours);
  return Number.isFinite(n) && n > 0 ? hoursClock(n) : '—';
}

/**
 * What a cell says when the line it holds is a flat AMOUNT rather than hours.
 *
 * `hoursClock(0)` is "0:00", and that is exactly what made a $50 reimbursement
 * read as a line worth nothing in the slot an approver reads a line's value
 * out of. Such a line has no hours to show — the money IS the line — so the
 * cell shows the money the server priced it at and no time figure at all.
 * The `$` is the unit marker; a bare 50 sitting in an hours column is the
 * plausible-looking wrong number CLAUDE.md calls the worst outcome here.
 */
function isAmountCell(cell) {
  return !!cell && !(Number(cell.hours) > 0) && Number(cell.pay) > 0;
}

/**
 * A DAY's pay figure, or a dash — and the difference between them matters.
 *
 * Found in a browser on 2026-09-10, not in jsdom: the phone's day strip
 * printed "$0.00" under Saturday and Sunday while the hours beside them
 * already read "—". `moneyText(0)` is "$0.00", and an empty column's total is
 * 0.
 *
 * That is the mistake this whole screen is written against, in miniature. A
 * zero is a CLAIM — this day was worked and came to nothing — and a day
 * nobody was on makes no claim at all. On a payroll screen the two must not
 * look the same.
 *
 * And the zero has to survive, because it is the alarming case: a day with an
 * hourly line on a worker who has no rate on file really does come to $0.00,
 * and hiding that would hide the reason somebody is about to be underpaid.
 * `lines` — the server's count of what is behind the column — is the only
 * thing that separates them, which is why it is on the wire rather than
 * guessed at here.
 */
function dayPayText(d) {
  if (!d || d.pay === null || d.pay === undefined) return null;
  if (d.lines === 0) return '—';
  return moneyText(d.pay);
}

function cellText(cell) {
  if (!cell) return '';
  if (isAmountCell(cell)) return moneyText(cell.pay) || '—';
  return cellHours(cell.hours);
}

/** A figure and its label, for the corner and the totals. */
function Figure({ label, value, money, testAttr }) {
  const attrs = testAttr ? { [testAttr]: '' } : {};
  return (
    <div className={`dp-fig${money ? ' dp-fig--money' : ''}`}>
      <span className="dp-fig__label">{label}</span>
      <span className="dp-fig__value fw-num" {...attrs}>{value}</span>
    </div>
  );
}

/**
 * The corner of the grid: what this week comes to.
 *
 * First on the screen at both widths, because it is the number being
 * authorised and because a total a person has to scroll nine cards to reach
 * is a total nobody reads.
 */
function Corner({ grid }) {
  const pay = moneyText(grid.total.pay);
  return (
    <div className="dp-grid__corner">
      <Figure label="Hours this week" value={hoursClock(grid.total.hours)} testAttr="data-grid-total-hours" />
      {pay ? <Figure label="Pay this week" value={pay} money testAttr="data-grid-total-pay" /> : null}
      {!grid.money ? (
        <div className="dp-grid__withheld">
          Pay is not shown — it needs the rates permission. The hours are all here.
        </div>
      ) : null}
    </div>
  );
}

/**
 * A row total, used on the right edge of the wide grid AND in the phone's
 * by-job summary. Same `data-row-hours` / `data-row-pay` attributes in both,
 * so one browser assertion covers both layouts rather than two that can drift.
 */
function RowTotal({ row }) {
  const pay = moneyText(row.pay);
  return (
    <>
      <span className="dp-grid__rowh fw-num" data-row-hours={row.key}>{cellHours(row.hours)}</span>
      {pay ? <span className="dp-grid__rowp fw-num" data-row-pay={row.key}>{pay}</span> : null}
    </>
  );
}

/**
 * THE GRID, when the container is wide enough to hold seven day columns.
 *
 * A real `<table>`, because it is one: the day headers and the row headers are
 * `<th scope>`, so a screen reader announces "Mow & edge, Tue 8, 4:00" instead
 * of reading a wall of numbers. The totals are a `<tfoot>`, which is what a
 * totals row is.
 *
 * Every track is `minmax(0, …)` and there is no `min-width` and no px width
 * anywhere in the stylesheet — the table is `table-layout: fixed` so seven
 * columns divide the space they have rather than demanding the space they
 * want. That is the difference between this and the Office Jobs table
 * measured at 683px inside a 360px viewport.
 */

/**
 * The row's name, as a BUTTON when it is a real job and somebody is listening.
 *
 * Owner, 2026-09-17: "MAKE THE FUCKING JOBS CLICKABLE EVERYWHERE". This grid is
 * the other half of the approval screen — the same week, laid out by job — and
 * the job names in it were plain text in both the wide table and the narrow
 * by-job list. A name you cannot press is the screen refusing the one question
 * it puts in front of you.
 *
 * Falls back to a plain span for a `line` row (a typed line has no job to open)
 * and whenever no handler was passed, so an approved sheet reads exactly as it
 * did rather than growing dead controls.
 */
function RowTitle({ row, onPeekJob }) {
  if (!onPeekJob || row.job_id == null) {
    return <span className="dp-grid__rowtitle">{row.title}</span>;
  }
  return (
    <button
      type="button"
      className="dp-grid__rowtitle dp-grid__rowtitle--tappable"
      data-peek-job={row.job_id}
      onClick={() => onPeekJob(row)}
    >
      {row.title}
    </button>
  );
}

function WideGrid({ grid, entryById, onPickLine, onPeekJob }) {
  const dayPayRow = grid.money;
  return (
    <div className="dp-grid__tablewrap">
      <table className="dp-grid__table">
        <caption className="fw-sr-only">
          Hours by job and day for this week, with totals per job, per day, and for the week.
        </caption>
        <thead>
          <tr>
            <th scope="col" className="dp-grid__lead">Job</th>
            {grid.days.map((d) => (
              <th
                scope="col"
                key={d.date}
                data-grid-daycol={d.date}
                className={`dp-grid__day${d.outside ? ' dp-grid__day--outside' : ''}`}
              >
                {dayShort(d.date) || d.date}
              </th>
            ))}
            <th scope="col" className="dp-grid__edge">Week</th>
          </tr>
        </thead>
        <tbody>
          {grid.rows.map((row) => (
            <tr key={row.key} data-grid-row={row.key}>
              <th scope="row" className="dp-grid__lead">
                <RowTitle row={row} onPeekJob={onPeekJob} />
                {row.subtitle ? <span className="dp-grid__rowsub">{row.subtitle}</span> : null}
              </th>
              {grid.days.map((d) => {
                const cell = row.cells[d.date];
                if (!cell) return <td key={d.date} className="dp-grid__cell dp-grid__cell--empty">—</td>;
                const entry = entryById.get(cell.id);
                const label = `${row.title}, ${dayLong(d.date) || d.date}, ${cellText(cell)}`;
                const inner = (
                  <span className={`dp-grid__val fw-num${isAmountCell(cell) ? ' dp-grid__val--money' : ''}`}>
                    {cellText(cell)}
                  </span>
                );
                return (
                  <td key={d.date} className={`dp-grid__cell${cell.adjusted ? ' dp-grid__cell--adj' : ''}`}>
                    {/* An approved sheet has no onPickLine, so the cell is a
                        plain figure. A button that does nothing is worse than
                        no button: it tells somebody the sheet is still open. */}
                    {onPickLine && entry ? (
                      <button
                        type="button"
                        className="dp-grid__pick"
                        data-grid-cell={String(cell.id)}
                        aria-label={label}
                        onClick={() => onPickLine(entry)}
                      >
                        {inner}
                      </button>
                    ) : (
                      <span className="dp-grid__pick dp-grid__pick--static" data-grid-cell={String(cell.id)}>{inner}</span>
                    )}
                  </td>
                );
              })}
              <td className="dp-grid__edge"><RowTotal row={row} /></td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row" className="dp-grid__lead">Hours</th>
            {grid.days.map((d) => (
              <td key={d.date} className="dp-grid__cell">
                <span className="fw-num" data-day-hours={d.date}>{cellHours(d.hours)}</span>
              </td>
            ))}
            <td className="dp-grid__edge">
              <span className="dp-grid__rowh fw-num">{hoursClock(grid.total.hours)}</span>
            </td>
          </tr>
          {dayPayRow ? (
            <tr className="dp-grid__payrow">
              <th scope="row" className="dp-grid__lead">Pay</th>
              {grid.days.map((d) => (
                <td key={d.date} className="dp-grid__cell">
                  <span className="fw-num" data-day-pay={d.date}>{dayPayText(d) || '—'}</span>
                </td>
              ))}
              <td className="dp-grid__edge">
                <span className="dp-grid__rowp fw-num">{moneyText(grid.total.pay) || '—'}</span>
              </td>
            </tr>
          ) : null}
        </tfoot>
      </table>
    </div>
  );
}

/**
 * The ROW edge on a phone: every job's week, in one short list.
 *
 * This is the half a day-grouped list loses. Grouping by day answers "is
 * Tuesday right" and cannot answer "is this job's week right" — and on a
 * fixed-price job the second is the one that matters, because the amount is
 * per LINE and a job split across three days is billed three times
 * (paystubCalc.computeFigures). That is invisible until the job's own total
 * is on the screen.
 */
function ByJob({ grid, onPeekJob }) {
  if (!grid.rows.length) return null;
  return (
    <div className="dp-grid__byjob">
      <SectionLabel>By job · {grid.rows.length}</SectionLabel>
      {grid.rows.map((row) => (
        <div className="dp-grid__jobrow" key={row.key} data-job-total={row.key}>
          <span className="dp-grid__jobname">
            <RowTitle row={row} onPeekJob={onPeekJob} />
            {row.subtitle ? <span className="dp-grid__rowsub">{row.subtitle}</span> : null}
          </span>
          <span className="dp-grid__jobfigs"><RowTotal row={row} /></span>
        </div>
      ))}
    </div>
  );
}

/**
 * The COLUMN edge, at both widths: one group per day that has lines, headed by
 * that day's own total, with the caller's own line markup inside it.
 *
 * The lines are rendered by `renderLine` rather than by this file on purpose.
 * SheetDetail already draws a line with its provenance badges, its adjustment
 * reason, its clock figure and its change history, and every one of those is
 * something an approver needs. Re-implementing them here would be a second
 * rendering of the same record that can disagree with the first.
 *
 * A day with NO lines gets no group. An empty card is not information, and
 * seven of them would bury the four days somebody actually worked — the
 * question "did anybody work Saturday" is answered by the day's own total,
 * which is on the wide grid's footer and in the strip below.
 */
function DayGroups({ days, byDay, renderLine }) {
  const worked = days.filter((d) => (byDay.get(d.date) || []).length > 0);
  if (!worked.length) return null;
  return (
    <div className="dp-grid__days">
      {worked.map((d) => {
        const pay = dayPayText(d);
        return (
          <section className="dp-grid__daygroup" key={d.date} data-day-group={d.date}>
            <header className={`dp-grid__dayhead${d.outside ? ' dp-grid__dayhead--outside' : ''}`}>
              <span className="dp-grid__dayname">
                {dayLong(d.date) || d.date}
                {d.outside ? <span className="dp-grid__outside">outside this week</span> : null}
              </span>
              {/* `data-daygroup-*`, NOT `data-day-*`. The canonical day total
                  is the strip's on a phone and the table footer's on a
                  desktop — exactly one per width. Two elements answering the
                  same selector is how a browser check ends up measuring
                  whichever one happened to be first in the DOM, which is a
                  check that cannot be relied on to be checking the thing. */}
              <span className="dp-grid__dayfigs">
                <span className="dp-grid__rowh fw-num" data-daygroup-hours={d.date}>{cellHours(d.hours)}</span>
                {pay ? <span className="dp-grid__rowp fw-num" data-daygroup-pay={d.date}>{pay}</span> : null}
              </span>
            </header>
            <div className="dp-cards">
              {(byDay.get(d.date) || []).map((entry) => (
                <React.Fragment key={entry.id}>{renderLine(entry)}</React.Fragment>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/**
 * The day totals for every day of the week, including the ones with no lines.
 *
 * Phone only — the wide layout has them in the table footer. It is a strip
 * rather than seven cards: "nobody worked Saturday" is a real answer and it
 * has to be visible, but it does not deserve a card each.
 */
function DayStrip({ grid }) {
  return (
    <div className="dp-grid__strip">
      {grid.days.map((d) => (
        <div className={`dp-grid__stripday${d.outside ? ' dp-grid__stripday--outside' : ''}`} key={d.date}>
          <span className="dp-grid__stripname">{dayShort(d.date) || d.date}</span>
          <span className="dp-grid__striph fw-num" data-day-hours={d.date}>{cellHours(d.hours)}</span>
          {dayPayText(d) ? (
            <span className="dp-grid__stripp fw-num" data-day-pay={d.date}>{dayPayText(d)}</span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * @param {object}   grid        `grid` off GET /api/timesheets/:id
 * @param {object[]} entries     the sheet's lines, for the detail under each day
 * @param {Function} renderLine  (entry) => node — the caller's own line markup
 * @param {Function} [onPickLine] (entry) => void; omit on an approved sheet
 * @param {Function} [onPeekJob]  (row) => void; makes job names pressable
 */
export default function TimesheetGrid({ grid, entries, renderLine, onPickLine, onPeekJob }) {
  const wrap = useRef(null);
  const narrow = useNarrowContainer(wrap);

  // The hook has to run before any early return, so `ok` is a branch rather
  // than a guard clause.
  const ok = !!grid && Array.isArray(grid.days) && Array.isArray(grid.rows) && !!grid.total;

  const entryById = new Map(list(entries).map((e) => [e.id, e]));

  // ── The day buckets come off the ENTRIES, never off the grid's cells ─────
  //
  // Two reasons, and the second is the one that matters.
  //
  // A response that carries no `grid` at all — a read that failed halfway, a
  // client running against a server that predates the field — must still show
  // every line. A component that grouped by the grid would render a blank
  // sheet in that case, and a blank timesheet is indistinguishable from an
  // empty week to the person looking at it.
  //
  // And if the grid ever DID omit a line, grouping by it would hide that line
  // from the only place it can be corrected while its hours stayed in the
  // total. Reading the entries means a line the grid forgot is still on the
  // screen, next to a total that will visibly disagree with it.
  const byDay = new Map();
  for (const entry of list(entries)) {
    const date = entry && entry.date;
    if (!date) continue;
    if (!byDay.has(date)) byDay.set(date, []);
    byDay.get(date).push(entry);
  }
  // Within a day, the order the sheet itself uses (date, created_at, id).
  for (const bucket of byDay.values()) bucket.sort((a, b) => a.id - b.id);

  // The week's own days first, then any day a line landed on that the grid
  // did not name — so no line can fall off the bottom of the screen.
  const named = new Set(ok ? grid.days.map((d) => d.date) : []);
  const dayOrder = [
    ...(ok ? grid.days : []),
    ...[...byDay.keys()].filter((d) => !named.has(d)).sort().map((date) => ({ date })),
  ];

  return (
    <div className="dp-grid" ref={wrap}>
      {ok ? (
        <>
          <Corner grid={grid} />
          {/* The corner is a header figure with no columns behind it — today
              that is exactly a sheet with no lines, whose total comes off
              timesheets.total_hours. Saying so is the difference between an
              honest total and one the columns quietly contradict. */}
          {!grid.reconciles ? (
            <Notice tone="warn">
              This week&apos;s total does not add up from the lines below — the sheet has no
              lines, so the figure is the one stored on the timesheet itself.
            </Notice>
          ) : null}
          {/* ── On a PHONE the by-job edge goes BELOW the lines ───────────
              Owner, 2026-09-18: "Have to scroll down and fucking look for the
              timesheet line item".

              MEASURED at 390x844 on /approve/1: the first line sat at y=975,
              131px below the fold, so nothing being approved was on screen at
              load. What was above it: a notice (64), the add controls (171),
              this grid's corner totals (71), ByJob (313) and the day strip
              (155). ByJob is the biggest single block and the only one that is
              a SUMMARY of the lines rather than the lines themselves. Moving
              it brought the first line to y=594 at 390px and y=731 at 320px.

              It is not dropped, and its reason is unchanged: a fixed-price job
              split across three days is billed three times
              (paystubCalc.computeFigures), and only the job's own weekly total
              shows that. It is the cross-check, and SheetDetail already states
              the rule this follows — "the thing being approved should not be
              underneath the argument for it". Here the argument was on top of
              the thing.

              The WIDE grid is untouched: at desktop width `WideGrid` puts both
              edges on the screen at once, which is the whole point of it, and
              nothing is below any fold. */}
          {narrow ? null : <WideGrid grid={grid} entryById={entryById} onPickLine={onPickLine} onPeekJob={onPeekJob} />}
          {narrow ? <DayStrip grid={grid} /> : null}
        </>
      ) : null}
      <DayGroups days={dayOrder} byDay={byDay} renderLine={renderLine} />
      {narrow && grid ? <ByJob grid={grid} onPeekJob={onPeekJob} /> : null}
    </div>
  );
}
